import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { BAND_QUOTES } from "./legacyQuotes";

/**
 * LegacyQuotesBand — a dark, brand-accented spotlight band (above the footer)
 * that auto-rotates through the 10 legacy quotes + 20 brand statements +
 * 10 Humanity First statements (40 in total).
 */
export function LegacyQuotesBand() {
    const [i, setI] = useState(0);
    const total = BAND_QUOTES.length;
    useEffect(() => {
        const t = setInterval(() => setI((v) => (v + 1) % total), 4500);
        return () => clearInterval(t);
    }, [total]);

    const navBtn = "inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/80 hover:bg-white/10 hover:text-white transition-colors";

    return (
        <section className="relative overflow-hidden bg-[#1B1033] py-16 sm:py-20" data-testid="legacy-quotes-band">
            <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-[#6D28D9]/30 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-[#1B5E20]/35 blur-3xl pointer-events-none" />
            <div className="relative max-w-4xl mx-auto px-5 text-center">
                <span className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.18em] text-[#C9A55B]">
                    <span className="h-px w-8 bg-[#C9A55B]/60" /> Words that carry our legacy <span className="h-px w-8 bg-[#C9A55B]/60" />
                </span>
                <Quote className="h-9 w-9 sm:h-10 sm:w-10 text-[#C9A55B] mx-auto mt-6 mb-4" />
                <div className="min-h-[128px] sm:min-h-[120px] flex items-center justify-center">
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={i}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -14 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="text-white font-semibold leading-snug max-w-3xl"
                            style={{ fontSize: "clamp(19px, 2.9vw, 31px)" }}
                            data-testid={`legacy-quote-${i}`}
                        >
                            &ldquo;{BAND_QUOTES[i]}&rdquo;
                        </motion.p>
                    </AnimatePresence>
                </div>
                <div className="mt-8 flex items-center justify-center gap-4">
                    <button type="button" onClick={() => setI((v) => (v - 1 + total) % total)} aria-label="Previous quote" data-testid="legacy-quote-prev" className={navBtn}>
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-[12.5px] font-semibold tracking-[0.12em] text-[#C9A55B] tabular-nums" data-testid="legacy-quote-counter">
                        {String(i + 1).padStart(2, "0")} / {total}
                    </span>
                    <button type="button" onClick={() => setI((v) => (v + 1) % total)} aria-label="Next quote" data-testid="legacy-quote-next" className={navBtn}>
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </section>
    );
}

export default LegacyQuotesBand;
